/**
 * ContactsTab — facility-contact CRUD (iter 170 split of AdminReferralCrm).
 *
 * Props:
 *   contacts:  Array<{id, name, role, email, phone}>
 *   onAdd:     async (payload) => void  — writes via POST /contacts
 *   onDelete:  async (contactId) => void — writes via DELETE /contacts/{id}
 *   busy:      boolean — disables buttons during a mutation
 *
 * The parent (``FacilityDrawer``) owns the axios calls + refresh so this
 * component stays fully presentational.
 */
import React, { useState } from 'react';
import { Plus, Trash2, Mail, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { TextField } from './uiKit';

export function ContactsTab({ contacts, onAdd, onDelete, busy }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', email: '', phone: '' });

  const submit = () => {
    onAdd(form);
    setForm({ name: '', role: '', email: '', phone: '' });
    setShowForm(false);
  };

  return (
    <div className="space-y-3">
      {contacts.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No contacts yet.</p>
      ) : contacts.map((c) => (
        <div
          key={c.id}
          className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 flex items-start justify-between gap-3"
          data-testid={`crm-contact-${c.id}`}
        >
          <div>
            <div className="font-medium text-slate-100">{c.name}</div>
            {c.role && <div className="text-xs text-slate-500">{c.role}</div>}
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-400">
              {c.email && (
                <span className="inline-flex items-center gap-1">
                  <Mail className="h-3 w-3" />{c.email}
                </span>
              )}
              {c.phone && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3 w-3" />{c.phone}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => onDelete(c.id)}
            className="text-rose-300 hover:text-rose-200 p-1"
            disabled={busy}
            title="Remove"
            data-testid={`crm-contact-delete-${c.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}

      {showForm ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-2">
          <TextField label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="crm-contact-name" />
          <TextField label="Role"   value={form.role} onChange={(v) => setForm({ ...form, role: v })} testId="crm-contact-role" />
          <TextField label="Email"  value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="crm-contact-email" />
          <TextField label="Phone"  value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="crm-contact-phone" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowForm(false)} size="sm" disabled={busy}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={busy || !form.name.trim()}
              data-testid="crm-add-contact-submit"
            >Add</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowForm(true)} data-testid="crm-add-contact">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add contact
        </Button>
      )}
    </div>
  );
}

export default ContactsTab;
